import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, useCallback } from 'react';

import { PaletteType, TextPalette } from '@Components/design-system/color-v2/types';
import Icon from '@Components/design-system/icon';
import Text from '@Components/design-system/text-v2';
import { FontType } from '@Components/design-system/text-v2/types';
import BottomNavbarWhatsAppIcon from '@Components/design-system/whatsapp-icon';
import { ROUTE_NAVIGATION } from '@Utils/constants';
import useAnasRouter from '@Utils/hooks/anas-router';
import { useTrans } from '@Utils/hooks/translation';

interface NavigationItem {
  NAME: string;
}

interface BottomNavbarProps {
  navigationArray?: NavigationItem[];
  children?: React.ReactNode;
  hide?: boolean;
  whatsappIcon?: boolean;
  whatsappIconPosition?: number;
}

const BottomNavbar: React.FC<BottomNavbarProps> = ({
  children,
  navigationArray = [],
  hide = false,
  whatsappIcon = false,
  whatsappIconPosition,
}) => {
  const pathname = usePathname().split('/')[1];
  const [visibility, setVisibility] = useState(!hide);
  const prevScrollPosRef = useRef(0);

  const handleScroll = useCallback(() => {
    const currentScrollPos = window.scrollY;
    setVisibility(currentScrollPos <= 0);
    prevScrollPosRef.current = currentScrollPos;
  }, []);

  useEffect(() => {
    if (hide) return;

    const debouncedHandleScroll = debounce(handleScroll, 20);
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [hide, handleScroll]);

  return (
    <div className='relative'>
      <main>
        <nav
          className={clsx(
            'fixed bottom-0 w-full md:w-128',
            'bg-white',
            'shadow-[0_-1px_2px_-1px_rgba(0,0,0,0.1)]',
            'transition-transform duration-300',
            'translate-y-0',
          )}>
          {whatsappIcon && <BottomNavbarWhatsAppIcon visibility={visibility} position={whatsappIconPosition} />}
          {children}
          <div className={`grid grid-cols-5 justify-stretch w-full py-2`}>
            {navigationArray.map(({ NAME: nav }) => (
              <NavItem key={nav} nav={nav} currentPath={pathname} />
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
};

const NavItem: React.FC<{ nav: string; currentPath: string }> = ({ nav, currentPath }) => {
  const isActive = ROUTE_NAVIGATION[nav]?.route === currentPath;
  const { t } = useTrans('dx');
  const router = useAnasRouter();

  return (
    <div
      onClick={() => router.push({ route: ROUTE_NAVIGATION[nav]?.route || '' })}
      className='flex flex-col items-center justify-center gap-1 p-1 overflow-hidden'>
      <Link className='hidden' href={`/${ROUTE_NAVIGATION[nav]?.route || ''}`} prefetch={true} />
      <Icon
        name={ROUTE_NAVIGATION[nav]?.icon}
        size='m'
        fill={isActive}
        color={isActive ? PaletteType.GREYS_600 : PaletteType.GREYS_600_40}
      />
      <Text
        font={isActive ? FontType.TINY_B : FontType.TINY_L}
        color={isActive ? TextPalette.DARK_1 : TextPalette.DARK_2}
        className='truncate overflow-hidden whitespace-nowrap text-ellipsis'>
        {t(`navigation.${ROUTE_NAVIGATION[nav]?.name?.toLowerCase()}`)}
      </Text>
    </div>
  );
};

export function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default BottomNavbar;
